import React, { useEffect, useRef, useState } from 'react';
import { X, Mail, Building2, Clock } from 'lucide-react';

/**
 * Input con autocompletado de destinatarios para composer de correo.
 *
 * Mezcla contactos del CRM + remitentes/destinatarios históricos del propio
 * usuario (endpoint `/api/correo/sugerir-destinatarios?q=...`).
 *
 * Soporta múltiples destinatarios separados con coma o Enter, mostrándose
 * como pills eliminables.
 *
 * Props:
 *   - value: string[] (lista de emails)
 *   - onChange: (string[]) => void
 *   - placeholder?: string
 *   - testid?: string  (prefijo para data-testid: `${testid}-input`, `${testid}-suggest-{i}`, etc.)
 */
export default function RecipientInput({ value = [], onChange, placeholder, testid = 'recipient' }) {
  const [draft, setDraft] = useState('');
  const [sugerencias, setSugerencias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onClickOut = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOut);
    return () => document.removeEventListener('mousedown', onClickOut);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!draft || draft.trim().length === 0) {
      setSugerencias([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/correo/sugerir-destinatarios?q=${encodeURIComponent(draft.trim())}&limit=8`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (r.ok) {
          const data = await r.json();
          // Filtra los que ya están en value para no sugerirlos de nuevo
          const ya = new Set(value.map((v) => v.toLowerCase()));
          const items = (data.items || []).filter((x) => !ya.has(x.email.toLowerCase()));
          setSugerencias(items);
          setHighlighted(0);
          setOpen(items.length > 0);
        }
      } catch (_) { /* noop */ }
      finally { setLoading(false); }
    }, 200);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [draft]);  // 'value' intencionadamente fuera para evitar re-fetch al añadir pill

  const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());

  const addEmail = (email) => {
    const e = (email || '').trim();
    if (!e) return false;
    if (!isEmail(e)) return false;
    if (value.some((v) => v.toLowerCase() === e.toLowerCase())) return false;
    onChange([...value, e]);
    setDraft('');
    setSugerencias([]);
    setOpen(false);
    return true;
  };

  const removeAt = (i) => {
    const next = value.slice();
    next.splice(i, 1);
    onChange(next);
  };

  const onKeyDown = (e) => {
    if (open && sugerencias.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlighted((h) => Math.min(h + 1, sugerencias.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlighted((h) => Math.max(h - 1, 0));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        addEmail(sugerencias[highlighted].email);
        return;
      }
      if (e.key === 'Escape') {
        setOpen(false);
        return;
      }
    }
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      e.preventDefault();
      addEmail(draft);
      return;
    }
    if (e.key === 'Backspace' && !draft && value.length > 0) {
      removeAt(value.length - 1);
    }
  };

  const onBlur = () => {
    // Si hay draft válido al perder foco, conviértelo en pill
    if (draft && isEmail(draft)) addEmail(draft);
  };

  return (
    <div className="relative" ref={wrapperRef} data-testid={`${testid}-wrapper`}>
      <div className="flex flex-wrap items-center gap-1 border border-ink-200 rounded px-2 py-1.5 min-h-[36px] bg-white focus-within:border-brand-400">
        {value.map((email, i) => (
          <span
            key={`${email}-${i}`}
            className="inline-flex items-center gap-1 bg-brand-50 text-brand-700 border border-brand-200 rounded px-2 py-0.5 text-xs"
            data-testid={`${testid}-pill-${i}`}
          >
            {email}
            <button
              type="button"
              onClick={() => removeAt(i)}
              className="hover:bg-brand-100 rounded"
              aria-label={`Quitar ${email}`}
              data-testid={`${testid}-remove-${i}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          className="flex-1 min-w-[140px] outline-none border-0 text-sm py-0.5 bg-transparent"
          placeholder={value.length === 0 ? (placeholder || 'nombre o email…') : ''}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          onFocus={() => { if (sugerencias.length > 0) setOpen(true); }}
          data-testid={`${testid}-input`}
          autoComplete="off"
        />
      </div>
      {open && sugerencias.length > 0 && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 bg-white border border-ink-200 rounded-md shadow-lg max-h-64 overflow-y-auto"
          data-testid={`${testid}-suggest-list`}
        >
          {sugerencias.map((s, i) => (
            <button
              key={`${s.email}-${i}`}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); addEmail(s.email); }}
              onMouseEnter={() => setHighlighted(i)}
              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${highlighted === i ? 'bg-brand-50' : 'hover:bg-ink-50'}`}
              data-testid={`${testid}-suggest-${i}`}
            >
              {s.fuente === 'crm' ? <Mail className="w-3.5 h-3.5 text-brand-500 shrink-0" /> : <Clock className="w-3.5 h-3.5 text-ink-400 shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="text-ink-900 font-medium truncate">
                  {s.nombre || s.email}
                  {s.nombre && <span className="text-ink-500 font-normal ml-1">&lt;{s.email}&gt;</span>}
                </div>
                {s.empresa && (
                  <div className="text-[11px] text-ink-500 truncate flex items-center gap-1">
                    <Building2 className="w-3 h-3" /> {s.empresa}
                  </div>
                )}
              </div>
              <span className={`text-[9px] uppercase tracking-wider ${s.fuente === 'crm' ? 'text-brand-600' : 'text-ink-400'}`}>
                {s.fuente === 'crm' ? 'CRM' : 'reciente'}
              </span>
            </button>
          ))}
        </div>
      )}
      {loading && draft && (
        <div className="absolute right-3 top-2 text-[10px] text-ink-400">buscando…</div>
      )}
    </div>
  );
}
